import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertCircle,
  Bot,
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Clock,
  Copy,
  Database,
  ExternalLink,
  FileText,
  FolderKanban,
  GitBranch,
  Layers3,
  MoreHorizontal,
  Plus,
  RefreshCw,
  ScrollText,
  Search,
  Settings2,
  Sparkles,
  SquarePen,
  User,
  Waypoints,
  Workflow,
  X,
  Zap,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import EditScheduleModal from '../configurations/ingestion/EditScheduleModal';
import LogsModal from './LogsModal';
import {
  fetchAvailableWorkflows,
  fetchWorkflowProjects,
  getAvailableWorkflowProjectUuid,
  workflowApiErrorMessage,
  type AvailableWorkflowDto,
  type WorkflowProjectDto,
} from '../workflowbuilder/postWorkflowService';
import './Workflows.css';
import WorkflowProjectsModal from './WorkflowProjectsModal';

const WORKFLOW_ICON_BY_NAME: Record<string, LucideIcon> = {
  Workflow,
  GitBranch,
  Waypoints,
  Zap,
  Layers3,
  Sparkles,
  Bot,
  Settings2,
  Database,
  FileText,
  FolderKanban,
};

function WorkflowIcon({ iconRef }: { iconRef: string | undefined }) {
  const name = (iconRef ?? 'Workflow').trim();
  const Icon = WORKFLOW_ICON_BY_NAME[name] ?? Workflow;
  return <Icon className="wf-flow-icon" strokeWidth={1.75} aria-hidden />;
}

function formatShortDate(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
  } catch {
    return '—';
  }
}

function isRunningStatus(status: string | undefined): boolean {
  return (status ?? '').trim().toLowerCase() === 'running';
}

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const DEFAULT_PAGE_SIZE = 10;

function CopyIdAction({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <button
      type="button"
      className={`wf-row-menu__btn${copied ? ' wf-row-menu__btn--copied' : ''}`}
      onClick={() => void handleCopy()}
      title={copied ? 'Copied to clipboard' : `Copy workflow ID: ${id}`}
      aria-label={copied ? 'Workflow ID copied' : 'Copy workflow ID'}
    >
      {copied ? <Check className="w-4 h-4" aria-hidden /> : <Copy className="w-4 h-4" aria-hidden />}
    </button>
  );
}

const Workflows: React.FC = () => {
  const navigate = useNavigate();

  const [nameFilter, setNameFilter] = useState('');
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<AvailableWorkflowDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<{
    id: string;
    sourceName: string;
    scheduleType: 'Manual';
    isActive: boolean;
  } | null>(null);

  const [showLogsModal, setShowLogsModal] = useState(false);
  const [activeLogs, setActiveLogs] = useState<{ workflowId: string; name: string } | null>(null);
  const [showProjectsModal, setShowProjectsModal] = useState(false);

  const [projects, setProjects] = useState<WorkflowProjectDto[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selectedProjectKey, setSelectedProjectKey] = useState<string | '__all__' | null>(null);

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      setProjects(await fetchWorkflowProjects());
    } catch {
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  const loadList = useCallback(async () => {
    setListError(null);
    setLoading(true);
    try {
      setRows(await fetchAvailableWorkflows(true));
    } catch (e) {
      setListError(workflowApiErrorMessage(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  const effectiveProjectKey = useMemo(() => {
    if (selectedProjectKey === '__all__') return '__all__';
    if (selectedProjectKey && projects.some((p) => p.uuid === selectedProjectKey)) return selectedProjectKey;
    if (projects.length > 0) return projects[0].uuid;
    return '__all__';
  }, [selectedProjectKey, projects]);

  const projectCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const w of rows) {
      const key = getAvailableWorkflowProjectUuid(w) || '__ungrouped__';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [rows]);

  const rowsForProject = useMemo(() => {
    if (effectiveProjectKey === '__all__') return rows;
    return rows.filter((w) => getAvailableWorkflowProjectUuid(w) === effectiveProjectKey);
  }, [rows, effectiveProjectKey]);

  const filtered = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return rowsForProject;
    return rowsForProject.filter((w) =>
      [w.name, w.description ?? '', w.status ?? '', w.id, w.created_by ?? ''].join(' ').toLowerCase().includes(q)
    );
  }, [rowsForProject, nameFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [nameFilter, pageSize, effectiveProjectKey]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const rangeStart = filtered.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, filtered.length);

  const runningCount = useMemo(
    () => rowsForProject.filter((w) => isRunningStatus(w.status)).length,
    [rowsForProject]
  );

  const projectNameByUuid = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of projects) map.set(p.uuid, p.name);
    return map;
  }, [projects]);

  const selectedProjectLabel =
    effectiveProjectKey === '__all__'
      ? 'All projects'
      : projects.find((p) => p.uuid === effectiveProjectKey)?.name ?? 'Project';

  const goToBuilder = (workflowId?: string) => {
    navigate(workflowId ? `/portal/flowbuilder/${encodeURIComponent(workflowId)}` : '/portal/flowbuilder');
  };

  const openScheduleModal = (wf: AvailableWorkflowDto) => {
    setSelectedWorkflow({
      id: wf.id,
      sourceName: wf.name,
      scheduleType: 'Manual',
      isActive: true,
    });
    setShowScheduleModal(true);
  };

  const openLogs = (wf: AvailableWorkflowDto) => {
    setActiveLogs({ workflowId: wf.id, name: wf.name });
    setShowLogsModal(true);
  };

  // v2 lives on its own route so nested FO routes stay copyable from the address bar.
  const openV2 = () => {
    navigate('/portal/orchestration/workflows');
  };

  return (
    <div className="wf-page">
      <button type="button" className="wf-beta-bar" onClick={openV2}>
        <span className="wf-beta-bar__icon" aria-hidden>
          <Sparkles />
        </span>
        <span className="wf-beta-bar__content">
          <span className="wf-beta-bar__title">
            Workflows v2 is now available
            <span className="wf-beta-badge">Beta</span>
          </span>
          <span className="wf-beta-bar__description">
            Explore the new workflow experience with an improved interface.
          </span>
        </span>
        <span className="wf-beta-bar__action">
          Try the beta
          <ChevronRight className="w-4 h-4" aria-hidden />
        </span>
      </button>

      <header className="wf-top">
        <div className="wf-top__inner">
          <div className="wf-top__content">
            <p className="wf-top__eyebrow">Automation</p>
            <h1 className="wf-top__title">Agent Orchestrations</h1>
            {!loading && (
              <div className="wf-top__stats" aria-label="Workflow summary">
                <span className="wf-top__stat">
                  <Workflow className="w-3.5 h-3.5" aria-hidden />
                  {rows.length} total
                </span>
                <span className="wf-top__stat">
                  <Layers3 className="w-3.5 h-3.5" aria-hidden />
                  {projects.length} project{projects.length === 1 ? '' : 's'}
                </span>
                {runningCount > 0 ? (
                  <span className="wf-top__stat wf-top__stat--live">
                    <Activity className="w-3.5 h-3.5" aria-hidden />
                    {runningCount} running
                  </span>
                ) : null}
              </div>
            )}
          </div>
          <div className="wf-top__actions">
            <button type="button" className="wf-btn wf-btn--ghost" onClick={() => setShowProjectsModal(true)}>
              <FolderKanban className="w-4 h-4" aria-hidden />
              Projects
            </button>
            <button type="button" className="wf-btn wf-btn--primary" onClick={() => goToBuilder()}>
              <Plus className="w-4 h-4" aria-hidden />
              New workflow
            </button>
          </div>
        </div>
      </header>

      <div className="wf-shell">
        <aside className="wf-projects" aria-label="Projects">
          <div className="wf-projects__head">
            <FolderKanban className="w-4 h-4" aria-hidden />
            <span>Projects</span>
          </div>
          <nav className="wf-projects__list">
            {loadingProjects ? (
              <p className="wf-projects__empty">Loading projects…</p>
            ) : (
              <>
                {projects.map((p) => {
                  const active = effectiveProjectKey === p.uuid;
                  const count = projectCounts.get(p.uuid) ?? 0;
                  return (
                    <button
                      key={p.uuid}
                      type="button"
                      className={`wf-projects__item${active ? ' wf-projects__item--active' : ''}`}
                      onClick={() => setSelectedProjectKey(p.uuid)}
                    >
                      <span className="wf-projects__item-icon" aria-hidden>
                        <FolderKanban strokeWidth={1.75} />
                      </span>
                      <span className="wf-projects__item-label">{p.name}</span>
                      <span className="wf-projects__item-count">{count}</span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  className={`wf-projects__item wf-projects__item--all${effectiveProjectKey === '__all__' ? ' wf-projects__item--active' : ''}`}
                  onClick={() => setSelectedProjectKey('__all__')}
                >
                  <span className="wf-projects__item-icon" aria-hidden>
                    <Layers3 strokeWidth={1.75} />
                  </span>
                  <span className="wf-projects__item-label">All projects</span>
                  <span className="wf-projects__item-count">{rows.length}</span>
                </button>
              </>
            )}
          </nav>
        </aside>

        <section className="wf-main" aria-label="Workflow registry">
          <div className="wf-main__head">
            <h2 className="wf-main__title">{selectedProjectLabel}</h2>
            <p className="wf-main__meta">
              {loading ? (
                'Loading…'
              ) : (
                <>
                  <Activity className="w-3.5 h-3.5" aria-hidden />
                  {rowsForProject.length} workflow{rowsForProject.length === 1 ? '' : 's'}
                  {runningCount > 0 ? ` · ${runningCount} running` : ''}
                </>
              )}
            </p>
          </div>

          <div className="wf-toolbar">
            <div className="wf-toolbar__search">
              <Search className="wf-toolbar__search-icon" aria-hidden />
              <input
                type="search"
                className="wf-toolbar__search-input"
                placeholder="Search by name, status, owner…"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                aria-label="Search workflows"
              />
              {nameFilter ? (
                <button
                  type="button"
                  className="wf-toolbar__search-clear"
                  onClick={() => setNameFilter('')}
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" aria-hidden />
                </button>
              ) : null}
            </div>

            <div className="wf-toolbar__controls">
              <label className="wf-toolbar__field">
                <span className="wf-toolbar__field-label">Per page</span>
                <span className="wf-toolbar__select-wrap">
                  <select
                    className="wf-toolbar__select"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    aria-label="Rows per page"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="wf-toolbar__select-chevron" aria-hidden />
                </span>
              </label>

              <button
                type="button"
                className="wf-toolbar__refresh"
                onClick={() => void loadList()}
                disabled={loading}
                title="Refresh list"
                aria-label="Refresh list"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
              </button>
            </div>
          </div>

          {listError ? (
            <div className="wf-alert" role="alert">
              <AlertCircle className="w-4 h-4 shrink-0" aria-hidden />
              <span>{listError}</span>
              <button type="button" className="wf-btn wf-btn--outline wf-btn--sm" onClick={() => void loadList()}>
                Retry
              </button>
            </div>
          ) : null}

          <div className="wf-table-card">
            {loading ? (
              <div className="wf-loading" aria-busy="true">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="wf-loading__row" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="wf-empty">
                <div className="wf-empty__icon-wrap" aria-hidden>
                  <Workflow strokeWidth={1.5} />
                </div>
                <p className="wf-empty__title">
                  {rowsForProject.length === 0 ? 'No workflows in this project' : 'No matching workflows'}
                </p>
                <p className="wf-empty__text">
                  {rowsForProject.length === 0
                    ? 'Create a workflow in the flow builder to see it listed here.'
                    : 'Try a different search term or switch projects.'}
                </p>
                {rows.length === 0 ? (
                  <button type="button" className="wf-btn wf-btn--primary" onClick={() => goToBuilder()}>
                    <Plus className="w-4 h-4" aria-hidden />
                    Create workflow
                  </button>
                ) : null}
              </div>
            ) : (
              <>
                <div className="wf-table-wrap">
                  <table className="wf-table">
                    <thead>
                      <tr>
                        <th>
                          <span className="wf-th">
                            <Workflow className="w-3.5 h-3.5" aria-hidden />
                            Workflow
                          </span>
                        </th>
                        <th className="wf-col-hide-md">
                          <span className="wf-th">
                            <FileText className="w-3.5 h-3.5" aria-hidden />
                            Description
                          </span>
                        </th>
                        <th>
                          <span className="wf-th">
                            <Activity className="w-3.5 h-3.5" aria-hidden />
                            Status
                          </span>
                        </th>
                        <th className="wf-col-num">
                          <span className="wf-th">
                            <Zap className="w-3.5 h-3.5" aria-hidden />
                            Runs
                          </span>
                        </th>
                        <th className="wf-col-hide-sm">
                          <span className="wf-th">
                            <Calendar className="w-3.5 h-3.5" aria-hidden />
                            Created
                          </span>
                        </th>
                        <th className="wf-col-hide-lg">
                          <span className="wf-th">
                            <User className="w-3.5 h-3.5" aria-hidden />
                            Owner
                          </span>
                        </th>
                        <th className="wf-col-actions">
                          <span className="wf-th">
                            <MoreHorizontal className="w-3.5 h-3.5" aria-hidden />
                            Actions
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((wf) => {
                        const running = isRunningStatus(wf.status);
                        const runs = wf.successful_runs ?? 0;
                        const errs = wf.errors_count ?? 0;
                        const statusLabel = (wf.status ?? 'Idle').trim();
                        const desc = wf.description?.trim() || 'No description';
                        const owner = (wf.created_by ?? '').trim() || '—';
                        const projectUuid = getAvailableWorkflowProjectUuid(wf);
                        const flowSubtitle =
                          effectiveProjectKey === '__all__'
                            ? projectNameByUuid.get(projectUuid) || 'Unassigned project'
                            : desc !== 'No description'
                              ? desc
                              : 'Post workflow';

                        return (
                          <tr key={wf.id} className={running ? 'wf-row--live' : undefined}>
                            <td>
                              <button type="button" className="wf-flow-cell" onClick={() => goToBuilder(wf.id)}>
                                <span className="wf-flow-cell__icon">
                                  <WorkflowIcon iconRef={wf.icon} />
                                </span>
                                <span className="wf-flow-cell__text">
                                  <span className="wf-flow-cell__name">{wf.name}</span>
                                  <span className="wf-flow-cell__sub">{flowSubtitle}</span>
                                </span>
                                <ExternalLink className="wf-flow-cell__arrow w-3.5 h-3.5" aria-hidden />
                              </button>
                            </td>
                            <td className="wf-col-hide-md">
                              <span className="wf-cell-muted" title={desc}>
                                {desc}
                              </span>
                            </td>
                            <td>
                              <span className={`wf-status ${running ? 'wf-status--live' : 'wf-status--idle'}`}>
                                <span className="wf-status__dot" aria-hidden />
                                {running ? 'Running' : statusLabel}
                              </span>
                            </td>
                            <td className="wf-col-num">
                              <span className="wf-runs">
                                <span className="wf-runs__ok">{runs}</span>
                                {errs > 0 ? <span className="wf-runs__bad">{errs} failed</span> : null}
                              </span>
                            </td>
                            <td className="wf-col-hide-sm">
                              <span className="wf-cell-muted">{formatShortDate(wf.created_on)}</span>
                            </td>
                            <td className="wf-col-hide-lg">
                              <span className="wf-owner" title={owner !== '—' ? owner : undefined}>
                                <User className="w-3.5 h-3.5 shrink-0 opacity-60" aria-hidden />
                                {owner}
                              </span>
                            </td>
                            <td className="wf-col-actions">
                              <div className="wf-row-menu">
                                <button
                                  type="button"
                                  className="wf-row-menu__btn"
                                  onClick={() => goToBuilder(wf.id)}
                                  title="Open in builder"
                                  aria-label={`Open ${wf.name}`}
                                >
                                  <SquarePen className="w-4 h-4" aria-hidden />
                                </button>
                                <button
                                  type="button"
                                  className="wf-row-menu__btn"
                                  onClick={() => openLogs(wf)}
                                  title="Execution logs"
                                  aria-label={`Logs for ${wf.name}`}
                                >
                                  <ScrollText className="w-4 h-4" aria-hidden />
                                </button>
                                <button
                                  type="button"
                                  className="wf-row-menu__btn"
                                  onClick={() => openScheduleModal(wf)}
                                  title="Schedule"
                                  aria-label={`Schedule ${wf.name}`}
                                >
                                  <Clock className="w-4 h-4" aria-hidden />
                                </button>
                                <CopyIdAction id={wf.id} />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <footer className="wf-foot" aria-label="Pagination">
                  <span className="wf-foot__range">
                    {rangeStart}–{rangeEnd} of {filtered.length}
                  </span>
                  <div className="wf-foot__nav">
                    <button
                      type="button"
                      className="wf-foot__btn"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="w-4 h-4" aria-hidden />
                    </button>
                    <span className="wf-foot__page">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      type="button"
                      className="wf-foot__btn"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      aria-label="Next page"
                    >
                      <ChevronRight className="w-4 h-4" aria-hidden />
                    </button>
                  </div>
                </footer>
              </>
            )}
          </div>
        </section>
      </div>

      {showScheduleModal && selectedWorkflow && (
        <EditScheduleModal
          prefill={selectedWorkflow}
          onClose={() => setShowScheduleModal(false)}
          onSubmit={() => setShowScheduleModal(false)}
        />
      )}

      {showLogsModal && activeLogs && (
        <LogsModal workflowId={activeLogs.workflowId} name={activeLogs.name} onClose={() => setShowLogsModal(false)} />
      )}

      {showProjectsModal ? (
        <WorkflowProjectsModal
          onClose={() => {
            setShowProjectsModal(false);
            void loadProjects();
          }}
        />
      ) : null}
    </div>
  );
};

export default Workflows;
